/**
 * Reschedule a task on its own page — opened from the deadline alert's
 * "Pick…" so the picker isn't stacked under the alert card. Shows what the
 * task is and how late it is next to the availability-aware picker, then
 * returns to where the user came from.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Clock, Loader2, MessageSquare, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import DeadlinePicker from "../components/DeadlinePicker";

function relative(deadline) {
  const d = new Date(deadline);
  if (isNaN(d)) return null;
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  const abs = Math.abs(mins);
  const span = abs < 60 ? `${abs} min` : abs < 48 * 60 ? `${Math.round(abs / 60)} h` : `${Math.round(abs / 1440)} days`;
  return mins < 0 ? { late: true, text: `Overdue by ${span}` } : { late: false, text: `Due in ${span}` };
}

export default function RescheduleTask() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || "/app/tasks";
  const [task, setTask] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/tasks/${id}`)
      .then((r) => { if (alive) setTask(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Task not found"); });
    return () => { alive = false; };
  }, [id]);

  const goBack = () => nav(backTo, { replace: true });

  const save = async (iso, opts) => {
    const payload = { deadline: iso };
    if (iso && opts?.sendInvite !== undefined) payload.send_calendar_invite = opts.sendInvite;
    try {
      await api.patch(`/tasks/${id}`, payload);
      toast.success(`Rescheduled to ${new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`);
      goBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the deadline");
    }
  };

  const rel = task?.deadline ? relative(task.deadline) : null;
  const source = [task?.sender_name || task?.client_name, task?.chat_name].filter((x, i, a) => x && a.indexOf(x) === i).join(" · ");
  const context = task?.summary || task?.original_text;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="reschedule-page">
      <button onClick={goBack} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft size={15} /> Back
      </button>

      <h1 className="text-[22px] font-bold text-slate-900 tracking-tight">Reschedule task</h1>
      <p className="text-[13px] text-slate-500 mt-1 mb-6">Pick a new time — free slots are suggested around your other tasks and busy time.</p>

      {error ? (
        <div className="p-6 border border-slate-200 rounded-xl bg-white text-[14px] text-slate-600">
          {error}. <button onClick={goBack} className="text-blue-600 font-semibold hover:underline">Go back</button>
        </div>
      ) : !task ? (
        <div className="p-10 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] gap-6 items-start">
          {/* What is being rescheduled */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 lg:sticky lg:top-6">
            {rel && (
              <div className={"inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] mb-3 " +
                (rel.late ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200")}>
                {rel.late ? <AlertTriangle size={12} /> : <Clock size={12} />} {rel.text}
              </div>
            )}
            <div className="text-[17px] font-bold text-slate-900 leading-snug">
              {task.task_no ? <span className="text-slate-400 font-semibold mr-1.5">#{task.task_no}</span> : null}
              {task.title || "Task"}
            </div>
            <dl className="mt-4 space-y-2.5 text-[13px]">
              {task.deadline && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Clock size={14} className="mt-0.5 text-slate-400 shrink-0" />
                  <span>Current deadline: <span className="font-semibold text-slate-800">
                    {new Date(task.deadline).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                  </span></span>
                </div>
              )}
              {(task.assignee || source) && (
                <div className="flex items-start gap-2 text-slate-600">
                  <User size={14} className="mt-0.5 text-slate-400 shrink-0" />
                  <span>{[task.assignee && `Assigned to ${task.assignee}`, source && `from ${source}`].filter(Boolean).join(" · ")}</span>
                </div>
              )}
              {context && (
                <div className="flex items-start gap-2 text-slate-600">
                  <MessageSquare size={14} className="mt-0.5 text-slate-400 shrink-0" />
                  <span className="italic line-clamp-6">{context}</span>
                </div>
              )}
            </dl>
          </div>

          {/* The picker, inline */}
          <DeadlinePicker inline taskId={task.id} currentDeadline={task.deadline} onCancel={goBack} onSelect={save} />
        </div>
      )}
    </div>
  );
}
